import { HomeStaticHero } from "@/components/HomeStaticHero";
import SEO from "@/components/SEO";
import { useLanguage } from "@/contexts/LanguageContext";
import { PRODUCT_BRAND, getSiteOrigin } from "@/config/platform";
import { lazy, Suspense, useEffect, useState } from "react";
import { useScrollTracking } from "@/hooks/useScrollTracking";
import { HERO_SLIDES } from "@/config/heroSlides";
import { initHeroRotation } from "@/utils/heroRotation";
import { createOrganizationSchema, createWebsiteSchema, createBreadcrumbSchema, createLocalBusinessSchemaEnhanced, createItemListSchema } from "@/utils/structuredData";

// Lazy load components below the fold for faster initial load
const SocialProofSection = lazy(() => import("@/components/SocialProofSection"));
const RelocationToolkitSection = lazy(() => import("@/components/RelocationToolkitSection"));
const DestinationsSection = lazy(() => import("@/components/DestinationsSection"));
const PortugalSpotlightSection = lazy(() => import("@/components/PortugalSpotlightSection"));
const ServicesSection = lazy(() => import("@/components/ServicesSection"));
const EducationalContent = lazy(() => import("@/components/EducationalContent"));
const InteractiveGuides = lazy(() => import("@/components/InteractiveGuides"));
const BlogPreview = lazy(() => import("@/components/BlogPreview"));
const CTASection = lazy(() => import("@/components/CTASection"));

const Index = () => {
  const { language, t } = useLanguage();
  useScrollTracking(); // Track scroll depth and time on page
  // Full load of / already has #static-hero outside #root (LCP). Client navigations
  // arrive in a document that omitted it, so the same hero has to mount here.
  const [mountReactHero] = useState(
    () => typeof document !== "undefined" && document.getElementById("static-hero") == null,
  );
  
  const title = language === 'pt' 
    ? `DENVORA Mobility | Residência Internacional, Vistos & Relocalização`
    : `DENVORA Mobility | International Residency, Visas & Relocation`;
    
  const description = language === 'pt'
    ? `DENVORA Mobility (marca DENVORA): consultoria de residência internacional — Golden Visa, D7 / retirement visa Portugal, D6 e pathways na Europa. Diagnóstico end-to-end para famílias e investidores.`
    : `DENVORA Mobility (DENVORA brand): international residency consultancy — Golden Visa, D7 / retirement visa Portugal, D6 and European pathways. End-to-end guidance for families and investors.`;

  const keywords = language === 'pt'
    ? "denvora, denvora mobility, residência internacional, vistos Europa, Portugal Espanha Itália Grécia, Golden Visa Portugal, Visto D7 Portugal, retirement visa Portugal, Visto D6 reagrupamento familiar, buyers agent Portugal, consultoria imobiliária internacional, toolkit relocalização, pathways residência, investir Portugal, comprar casa Portugal, residência Portugal, visto nómada digital D8, consultoria Golden Visa"
    : "denvora, denvora mobility, international residency, Europe visas, Portugal Spain Italy Greece, Golden Visa Portugal, D7 Visa Portugal, retirement visa Portugal, D6 family reunification visa, buyers agent Portugal, international real estate consultancy, relocation toolkit, residency pathways, invest Portugal, buy property Portugal, Portugal residency, digital nomad D8 visa, Golden Visa consultancy";

  const baseUrl = getSiteOrigin(language === "pt" ? "pt" : "en");
  
  const services = language === 'pt' 
    ? [
        { name: "Residência internacional", description: "Pathways para Portugal, Espanha, Itália e Grécia", url: `${baseUrl}/pathways` },
        { name: "Golden Visa Portugal", description: "Residência por investimento — mercado flagship", url: `${baseUrl}/destinations/portugal/visto-gold` },
        { name: "Hub Espanha", description: "Vistos e residência na Espanha", url: `${baseUrl}/destinations/spain` },
        { name: "Hub Itália", description: "Vistos e residência em Itália", url: `${baseUrl}/destinations/italy` },
        { name: "Hub Grécia", description: "Vistos e residência na Grécia", url: `${baseUrl}/destinations/greece` },
        { name: "Toolkit de relocalização", description: "Ferramentas guiadas multi-destino", url: `${baseUrl}/interactive` }
      ]
    : [
        { name: "International residency", description: "Pathways for Portugal, Spain, Italy, and Greece", url: `${baseUrl}/pathways` },
        { name: "Golden Visa Portugal", description: "Investment residency — flagship market", url: `${baseUrl}/destinations/portugal/golden-visa` },
        { name: "Spain hub", description: "Visas and residency in Spain", url: `${baseUrl}/destinations/spain` },
        { name: "Italy hub", description: "Visas and residency in Italy", url: `${baseUrl}/destinations/italy` },
        { name: "Greece hub", description: "Visas and residency in Greece", url: `${baseUrl}/destinations/greece` },
        { name: "Relocation toolkit", description: "Guided multi-destination tools", url: `${baseUrl}/interactive` }
      ];

  const structuredData = [
    createOrganizationSchema(),
    createWebsiteSchema(),
    createLocalBusinessSchemaEnhanced(),
    // Review schema removed - add back when real reviews/ratings are available
    createItemListSchema(services),
    createBreadcrumbSchema([
      { name: language === 'pt' ? "Início" : "Home", url: `${baseUrl}/` }
    ])
  ];

  useEffect(() => {
    const hero = document.getElementById('static-hero');
    if (!hero) return;
    const headline = hero.querySelector<HTMLElement>("[data-static-hero-headline]");
    const subtitle = hero.querySelector<HTMLElement>("p:not([data-static-hero-headline])");
    const primaryCta = hero.querySelector('#hero-cta-primary');
    const secondaryCta = hero.querySelector('#hero-cta-secondary');
    if (headline) headline.textContent = t("hero.title");
    if (subtitle) subtitle.textContent = t("hero.subtitle");
    if (primaryCta) {
      primaryCta.textContent = t("hero.cta");
      primaryCta.setAttribute("href", "/pathways");
    }
    if (secondaryCta) {
      secondaryCta.textContent = t("hero.ctaSecondary");
      secondaryCta.setAttribute("href", "/interactive");
    }

    const lang = language === "en" ? "en" : "pt";
    const activeSlide = hero.querySelector<HTMLImageElement>(".hero-slide.is-active");
    if (activeSlide) activeSlide.alt = HERO_SLIDES[0].alt[lang];
  }, [language, t]);

  useEffect(() => {
    const lang = language === "en" ? "en" : "pt";
    return initHeroRotation(lang);
  }, [language]);

  return (
    <>
      <SEO 
        title={title}
        description={description}
        keywords={keywords}
        url="/"
        structuredData={structuredData}
      />
      
      {mountReactHero ? <HomeStaticHero /> : null}
      <Suspense fallback={null}>
        <DestinationsSection />
        <RelocationToolkitSection />
        <SocialProofSection />
        <ServicesSection />
        <EducationalContent />
        <InteractiveGuides />
        <PortugalSpotlightSection />
        <BlogPreview />
        <CTASection />
      </Suspense>
    </>
  );
};

export default Index;
